import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * ValidationResults Component (Placeholder)
 * 
 * This component will display validation results for uploaded artifacts.
 * For now, it accepts basic validation data and displays it.
 * 
 * Future: Will be expanded when backend provides detailed validation data.
 */
export default function ValidationResults({ validation = null, stepName }) {
  // If no validation data, show placeholder
  if (!validation) {
    return (
      <Card className="border-2 border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg">Validation Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-3 text-gray-500">
            <Info className="h-5 w-5" />
            <p className="text-sm">
              Artifact validation will appear here once analysis is complete.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Determine validation status
  const { status, messages = [], warnings = [], errors = [] } = validation;
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const isPassed = status === 'passed' || (!hasErrors && !hasWarnings);

  return (
    <Card
      className={`border-2 ${
        isPassed
          ? 'border-green-200 bg-green-50/30'
          : hasErrors
          ? 'border-red-200 bg-red-50/30'
          : 'border-amber-200 bg-amber-50/30'
      }`}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Validation Results</CardTitle>
          <Badge
            className={
              isPassed
                ? 'bg-green-100 text-green-700'
                : hasErrors
                ? 'bg-red-100 text-red-700'
                : 'bg-amber-100 text-amber-700'
            }
          >
            {isPassed ? (
              <CheckCircle className="h-4 w-4 mr-1" />
            ) : hasErrors ? (
              <XCircle className="h-4 w-4 mr-1" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-1" />
            )}
            {isPassed ? 'Passed' : hasErrors ? 'Failed' : 'Warnings'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Success Messages */}
          {isPassed && messages.length === 0 && (
            <div className="flex items-start space-x-3 text-green-700">
              <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Validation Passed</p>
                <p className="text-sm">All checks passed successfully for {stepName}.</p>
              </div>
            </div>
          )}

          {/* Info Messages */}
          {messages.map((msg, index) => (
            <div key={index} className="flex items-start space-x-3 text-blue-700">
              <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{msg}</p>
            </div>
          ))}

          {/* Warnings */}
          {warnings.map((warning, index) => (
            <div key={index} className="flex items-start space-x-3 text-amber-700">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{warning}</p>
            </div>
          ))}

          {/* Errors */}
          {errors.map((error, index) => (
            <div key={index} className="flex items-start space-x-3 text-red-700">
              <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
